import { useEffect, type PropsWithChildren, type ReactNode } from "react";

import clsx from "clsx";
import {
  NavLink as ReactRouterNavLink,
  useMatch,
  useNavigate,
  useResolvedPath,
  type NavLinkProps as ReactRouterNavLinkProps,
} from "react-router-dom";
import { tinykeys } from "tinykeys";

import { NavLinkContent } from "./nav-link-content";
import { Tooltip } from "./tooltip";

interface Props
  extends Omit<ReactRouterNavLinkProps, "className" | "children"> {
  isExpanded: boolean;
  icon: ReactNode;
  shortcut?: string;
  shortcutTo?: string;
}

export const NavLink = ({
  isExpanded,
  icon,
  children,
  shortcut,
  shortcutTo,
  ...props
}: PropsWithChildren<Props>) => {
  const navigate = useNavigate();
  const { pathname } = useResolvedPath(shortcutTo ?? props.to);
  const isMatch = useMatch(pathname);

  useEffect(() => {
    const unsubscribe = tinykeys(window, {
      [`$mod+${shortcut}`]: (event) => {
        event.preventDefault();
        if (!isMatch) {
          navigate(shortcutTo ?? props.to);
        }
      },
    });

    return () => {
      unsubscribe();
    };
  }, [isMatch]);

  return (
    <Tooltip content={isExpanded ? null : children} shortcut={shortcut}>
      <ReactRouterNavLink
        className={clsx("block no-underline", {
          "m-1.5": !isExpanded,
        })}
        {...props}
      >
        {({ isActive }) => (
          <NavLinkContent
            isActive={isActive}
            isExpanded={isExpanded}
            icon={icon}
            shortcut={shortcut}
          >
            {children}
          </NavLinkContent>
        )}
      </ReactRouterNavLink>
    </Tooltip>
  );
};
